import React from 'react';
import { Stack, Chip } from '@mui/material';

// Single source of truth for attendance status colours/labels — shared by the
// calendar tiles, the day-detail drawer and the header legend, so the codes
// (P / A / L / …) and colours never drift between places.
export const STATUS_META = {
  PRESENT:   { code: 'P',  label: 'Present',     bg: '#E8F5E9', text: '#1B5E20' },
  ABSENT:    { code: 'LOP', label: 'LOP / Absent', bg: '#FFEBEE', text: '#B71C1C' },
  LEAVE:     { code: 'L',  label: 'On Leave',    bg: '#FFEBEE', text: '#B71C1C' },
  ON_DUTY:   { code: 'OD', label: 'On Duty',     bg: '#E0F2F1', text: '#004D40' },
  COMP_OFF:  { code: 'CO', label: 'Comp Off',    bg: '#FFF8E1', text: '#7C5800' },
  HOLIDAY:   { code: 'H',  label: 'Holiday',     bg: '#ECEFF1', text: '#37474F' },
  HALF_DAY:  { code: 'HD', label: 'Half Day',    bg: '#F3E5F5', text: '#4A148C' },
  // A half-day *leave* — the employee is on leave for one session only. The
  // backend stores it as a full-day LEAVE row with is_half_day=true (there is
  // no half-leave status), so we surface it as its own chip instead of a plain
  // "On Leave". Amber = a half of the red "On Leave".
  HALF_DAY_LEAVE: { code: '½L', label: 'Half Day Leave', bg: '#FFF3E0', text: '#B45309' },
  NOT_RECORDED: { code: 'NR', label: 'Not recorded', bg: '#FAFAFA', text: '#9E9E9E' },
  NOT_APPLICABLE: { code: '·', label: 'Before joining / out of range', bg: '#F5F5F5', text: '#BDBDBD' },
};

// Resolve the display meta for an attendance cell/entry. A half-day leave keeps
// status==='LEAVE' but carries is_half_day=true, so map it to its own visual;
// everything else falls back to the plain status meta.
export const metaForStatus = (status, isHalfDay = false) =>
  (status === 'LEAVE' && isHalfDay)
    ? STATUS_META.HALF_DAY_LEAVE
    : (STATUS_META[status] || STATUS_META.NOT_RECORDED);

// The horizontal row of status chips. `compact` shrinks them for tight spots
// (header / inline panel); `nowrap` keeps them on one line (header); `justify`
// controls alignment within the container.
export const AttendanceLegend = ({ compact = true, nowrap = false, justify = 'flex-end', sx }) => (
  <Stack
    direction="row"
    spacing={compact ? 0.75 : 1}
    flexWrap={nowrap ? 'nowrap' : 'wrap'}
    useFlexGap
    sx={{ justifyContent: justify, ...sx }}
  >
    {Object.entries(STATUS_META).map(([key, meta]) => (
      <Chip
        key={key}
        size="small"
        label={`${meta.code} ${meta.label}`}
        sx={{
          bgcolor: meta.bg,
          color: meta.text,
          fontWeight: 600,
          fontSize: compact ? 10.5 : 12,
          height: compact ? 22 : 24,
          whiteSpace: 'nowrap',
        }}
      />
    ))}
  </Stack>
);

export default AttendanceLegend;
